import { useEffect, useState } from 'react';
import { procApi } from '../../api/procurement.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function Inventory() {
  const { can } = useAuth();
  const [tab, setTab] = useState('levels');
  return (
    <section>
      <header className="page-header"><h1>Inventory</h1></header>
      <div className="tabs">
        <button className={tab === 'levels' ? 'tab active' : 'tab'} onClick={() => setTab('levels')}>Stock Levels</button>
        <button className={tab === 'low' ? 'tab active' : 'tab'} onClick={() => setTab('low')}>Low Stock</button>
        <button className={tab === 'movements' ? 'tab active' : 'tab'} onClick={() => setTab('movements')}>Movements</button>
        <button className={tab === 'valuation' ? 'tab active' : 'tab'} onClick={() => setTab('valuation')}>Valuation</button>
        {can('stock_level', 'transfer') && <button className={tab === 'transfer' ? 'tab active' : 'tab'} onClick={() => setTab('transfer')}>Transfer</button>}
        {can('stock_level', 'adjust') && <button className={tab === 'adjust' ? 'tab active' : 'tab'} onClick={() => setTab('adjust')}>Adjustment</button>}
      </div>
      {tab === 'levels' && <StockLevels />}
      {tab === 'low' && <LowStock />}
      {tab === 'movements' && <Movements />}
      {tab === 'valuation' && <Valuation />}
      {tab === 'transfer' && <Transfer />}
      {tab === 'adjust' && <Adjustment />}
    </section>
  );
}

function StockLevels() {
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({ warehouse: '', search: '' });

  function refresh() {
    procApi.stockLevels(filters).then((d) => setItems(d?.items || []));
  }
  useEffect(refresh, [filters]);
  useEffect(() => { procApi.warehouses().then((d) => setWarehouses(d?.items || [])); }, []);

  return (
    <div className="card">
      <div className="grid-form">
        <label>Warehouse
          <select value={filters.warehouse} onChange={(e) => setFilters({ ...filters, warehouse: e.target.value })}>
            <option value="">All</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>
        <label>Search<input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="SKU or name" /></label>
      </div>
      <table className="data-table">
        <thead><tr><th>SKU</th><th>Product</th><th>Warehouse</th><th>On hand</th><th>Reserved</th><th>Available</th><th>Reorder Point</th></tr></thead>
        <tbody>
          {items.map((s) => {
            const low = Number(s.qty_on_hand) <= Number(s.reorder_point);
            return (
              <tr key={s.id} className={low ? 'low-stock' : ''}>
                <td><code>{s.sku}</code></td>
                <td>{s.product_name}</td>
                <td>{s.warehouse_name}</td>
                <td><strong>{Number(s.qty_on_hand).toFixed(2)}</strong> {s.unit}</td>
                <td>{Number(s.qty_reserved).toFixed(2)}</td>
                <td>{Number(s.qty_available).toFixed(2)}</td>
                <td className={low ? 'danger' : ''}>{Number(s.reorder_point).toFixed(2)}</td>
              </tr>
            );
          })}
          {items.length === 0 && <tr><td colSpan={7} className="muted">No stock records.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function LowStock() {
  const [items, setItems] = useState([]);
  useEffect(() => { procApi.lowStock().then((d) => setItems(d?.items || [])); }, []);
  return (
    <div className="card">
      <h3>Items at or below reorder point ({items.length})</h3>
      {items.length === 0 ? <div className="muted">All stock levels are healthy.</div> : (
        <table className="data-table">
          <thead><tr><th>SKU</th><th>Product</th><th>Warehouse</th><th>On hand</th><th>Reorder point</th><th>Suggested order</th></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="low-stock">
                <td><code>{s.sku}</code></td>
                <td>{s.product_name}</td>
                <td>{s.warehouse_name}</td>
                <td className="danger">{Number(s.qty_on_hand).toFixed(2)} {s.unit}</td>
                <td>{Number(s.reorder_point).toFixed(2)}</td>
                <td><strong>{Number(s.reorder_qty).toFixed(2)}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Movements() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  useEffect(() => { procApi.products({ active: true }).then((d) => setProducts(d?.items || [])); }, []);
  useEffect(() => { procApi.movements({ product: productId || undefined, limit: 200 }).then((d) => setItems(d?.items || [])); }, [productId]);

  return (
    <div className="card">
      <div className="grid-form">
        <label className="full">Product
          <select value={productId} onChange={(e) => setProductId(e.target.value)}>
            <option value="">All</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
          </select>
        </label>
      </div>
      <ol className="timeline">
        {items.map((m) => (
          <li key={m.id} className={`tl-${m.movement_type === 'in' ? 'approved' : m.movement_type === 'out' ? 'rejected' : 'pending'}`}>
            <div className="tl-dot" />
            <div className="tl-content">
              <strong>{m.movement_type.toUpperCase()}</strong> {Number(m.qty) > 0 ? '+' : ''}{Number(m.qty).toFixed(2)} {productId ? '' : `· ${m.product_name}`}
              <div className="muted small">{m.warehouse_name} · {new Date(m.moved_at).toLocaleString()} {m.moved_by_name ? `· by ${m.moved_by_name}` : ''}</div>
              {m.reference_type && <div className="muted small">ref: {m.reference_type} {m.reference_id?.slice(0, 8)}</div>}
              {m.notes && <div className="muted small">"{m.notes}"</div>}
            </div>
          </li>
        ))}
        {items.length === 0 && <div className="muted">No movements.</div>}
      </ol>
    </div>
  );
}

function Valuation() {
  const [data, setData] = useState(null);
  const [method, setMethod] = useState('average');
  useEffect(() => { procApi.valuation({ method }).then(setData); }, [method]);
  return (
    <div className="card">
      <div className="grid-form">
        <label>Method
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="average">Average cost</option>
            <option value="fifo">FIFO (first-in)</option>
          </select>
        </label>
      </div>
      {!data ? <div className="muted">Loading…</div> : (
        <>
          <p className="muted">Total stock value: <strong>{data.totalValue.toFixed(2)}</strong> ({data.items.length} SKUs)</p>
          <table className="data-table">
            <thead><tr><th>SKU</th><th>Product</th><th>Qty</th><th>Unit cost</th><th>Value</th></tr></thead>
            <tbody>
              {data.items.map((it, i) => (
                <tr key={i}><td><code>{it.sku}</code></td><td>{it.product_name}</td><td>{Number(it.qty_on_hand).toFixed(2)}</td><td>{Number(it.unit_cost).toFixed(4)}</td><td>{Number(it.value).toFixed(2)}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function Transfer() {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    procApi.products({ active: true }).then((d) => setProducts(d?.items || []));
    procApi.warehouses().then((d) => setWarehouses(d?.items || []));
  }, []);
  async function submit(e) {
    e.preventDefault();
    setError(null); setInfo(null);
    const fd = new FormData(e.currentTarget);
    try {
      await procApi.transfer({
        productId: fd.get('product'),
        fromWarehouseId: fd.get('from'),
        toWarehouseId: fd.get('to'),
        qty: Number(fd.get('qty')),
        notes: fd.get('notes') || null,
      });
      setInfo('Transfer recorded');
      e.currentTarget.reset();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }
  return (
    <form onSubmit={submit} className="card form-card grid-form">
      <h3 className="full">Stock Transfer</h3>
      {error && <div className="alert error full">{error}</div>}
      {info && <div className="alert info full">{info}</div>}
      <label className="full">Product
        <select name="product" required>
          <option value="">Select…</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
        </select>
      </label>
      <label>From
        <select name="from" required>
          <option value="">Select…</option>
          {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </label>
      <label>To
        <select name="to" required>
          <option value="">Select…</option>
          {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </label>
      <label>Quantity<input name="qty" type="number" step="0.01" required /></label>
      <label className="full">Notes<input name="notes" /></label>
      <div className="full actions"><button className="btn primary" type="submit">Transfer</button></div>
    </form>
  );
}

function Adjustment() {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    procApi.products({ active: true }).then((d) => setProducts(d?.items || []));
    procApi.warehouses().then((d) => setWarehouses(d?.items || []));
  }, []);
  async function submit(e) {
    e.preventDefault();
    setError(null); setInfo(null);
    const fd = new FormData(e.currentTarget);
    try {
      const r = await procApi.adjustment({
        productId: fd.get('product'),
        warehouseId: fd.get('warehouse'),
        newQtyOnHand: Number(fd.get('newQty')),
        reason: fd.get('reason'),
      });
      setInfo(`Adjusted by ${r.delta}`);
      e.currentTarget.reset();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }
  return (
    <form onSubmit={submit} className="card form-card grid-form">
      <h3 className="full">Stock Adjustment</h3>
      {error && <div className="alert error full">{error}</div>}
      {info && <div className="alert info full">{info}</div>}
      <label className="full">Product
        <select name="product" required>
          <option value="">Select…</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
        </select>
      </label>
      <label>Warehouse
        <select name="warehouse" required>
          <option value="">Select…</option>
          {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </label>
      <label>New qty on hand<input name="newQty" type="number" step="0.01" required /></label>
      <label className="full">Reason<input name="reason" required placeholder="cycle count, damage, etc." /></label>
      <div className="full actions"><button className="btn primary" type="submit">Apply Adjustment</button></div>
    </form>
  );
}
